import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import MuxPlayer from "@mux/mux-player-react/lazy";
import axios from "axios";
import { API_URL } from "@shared/util/env";
import { fmtTC } from "@shared/util/brand";
import { BRAND } from "@shared/brand";

const GUEST_NAME = "Guest";

const pad2 = (n) => String(n).padStart(2, "0");

function nextDropCountdown() {
  const now = new Date();
  const next = new Date(now);
  next.setHours(6, 0, 0, 0);
  if (now.getHours() >= 6) next.setDate(next.getDate() + 1);
  const ms = Math.max(0, next - now);
  const totalMin = Math.floor(ms / 60000);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return `${hh}h ${pad2(mm)}m`;
}

function avatarInitials(name) {
  const n = (name || "").trim();
  if (!n) return "G";
  return n.charAt(0).toUpperCase();
}

function dateString() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function timecodeToSeconds(tc) {
  if (!tc) return 0;
  const parts = tc.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

const DEFAULT_CURATOR_ID =
  import.meta.env.VITE_DRIP_CURATOR_ID || BRAND.communityDripCuratorId;

export default function CommunityDrip() {
  const { curatorId: paramCuratorId } = useParams();
  const curatorId = paramCuratorId || DEFAULT_CURATOR_ID;
  const [headlines, setHeadlines] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mode, setMode] = useState("watch");
  const [comments, setComments] = useState([]);
  const [threadInput, setThreadInput] = useState("");
  const [qaInput, setQaInput] = useState("");
  const [qaLoading, setQaLoading] = useState(false);
  const [qaMode, setQaMode] = useState("ai");
  const [qaLog, setQaLog] = useState([
    { who: "bot", text: "Ask me anything about the archive. I'll answer using the video library." },
  ]);
  const [dropTime, setDropTime] = useState(nextDropCountdown);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [curatorName, setCuratorName] = useState("");
  const threadRef = useRef(null);
  const qaRef = useRef(null);
  const playerRef = useRef(null);
  const apiBase = API_URL || `${window.location.origin}/api`;
  const active = headlines[activeIdx] || null;
  const libraryLabel = curatorName ? `${curatorName}'s Video Library` : "Video Library";

  useEffect(() => {
    axios.get(`${apiBase}/drip/curator/${curatorId}`)
      .then((res) => setCuratorName(res.data.name || ""))
      .catch(() => {});
  }, [curatorId, apiBase]);

  useEffect(() => {
    async function fetchHeadlines() {
      try {
        setLoading(true);
        const res = await axios.get(`${apiBase}/drip/headlines/${curatorId}`);
        const data = res.data;
        if (data.headlines && data.headlines.length > 0) {
          setHeadlines(data.headlines);
          setActiveIdx(0);
        } else {
          setError("No headlines available today.");
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError("No headlines generated for today yet.");
        } else {
          setError("Failed to load headlines.");
        }
        console.error("[drip] fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchHeadlines();
  }, [curatorId]);

  useEffect(() => {
    async function fetchComments() {
      if (!active) return;
      try {
        const res = await axios.get(`${apiBase}/drip/comments/${curatorId}/${activeIdx}`);
        setComments(res.data.map((c) => ({ name: c.userName, text: c.text })));
      } catch {
        setComments([]);
      }
    }
    fetchComments();
  }, [curatorId, activeIdx, active]);

  useEffect(() => {
    const id = setInterval(() => setDropTime(nextDropCountdown()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (active) setMode("watch");
  }, [activeIdx]);

  // Pause video when tab becomes hidden to prevent background streaming costs
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && playerRef.current && !playerRef.current.paused) {
        playerRef.current.pause();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      try { playerRef.current?.pause(); } catch {}
    };
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo(0, threadRef.current.scrollHeight);
  }, [comments]);

  useEffect(() => {
    qaRef.current?.scrollTo(0, qaRef.current.scrollHeight);
  }, [qaLog]);

  const switchHeadline = useCallback(
    (idx) => {
      setActiveIdx(idx);
    },
    []
  );

  const sendThread = async () => {
    if (!threadInput.trim() || !active) return;
    const text = threadInput.trim();
    setThreadInput("");
    setComments((prev) => [...prev, { name: GUEST_NAME, text }]);
    try {
      await axios.post(`${apiBase}/drip/comments/${curatorId}/${activeIdx}`, {
        userName: GUEST_NAME,
        text,
      });
    } catch (err) {
      console.error("[drip] post comment error:", err);
    }
  };

  const sendQA = async () => {
    if (!qaInput.trim() || !active || qaLoading) return;
    const q = qaInput.trim();
    setQaLog((prev) => [...prev, { who: "you", text: q }]);
    setQaInput("");
    setQaLoading(true);
    try {
      const res = await axios.post(`${apiBase}/drip/chat/${curatorId}`, {
        question: q,
        headlineTitle: active.title,
        headlineQuote: active.quote,
      });
      setQaLog((prev) => [...prev, { who: "bot", text: res.data.answer }]);
    } catch (err) {
      setQaLog((prev) => [...prev, { who: "bot", text: "Sorry, something went wrong. Try again." }]);
      console.error("[drip] chat error:", err);
    } finally {
      setQaLoading(false);
    }
  };

  const windowLabel =
    active?.windowStart && active?.windowEnd
      ? `${fmtTC(active.windowStart)}–${fmtTC(active.windowEnd)}`
      : fmtTC(active?.windowStart || "");

  if (loading) {
    return (
      <>
        <style>{CSS_TEXT}</style>
        <main>
          <div className="topbar">
            <div className="topbar-inner">
              <div className="brand">
                <div className="logo">✦</div>
                <div>
                  <h1 style={{ margin: 0, fontSize: 16, lineHeight: 1.1 }}>VaultBot Daily Drip</h1>
                  <div className="sub">{libraryLabel}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="wrap">
            <div className="card" style={{ textAlign: "center", padding: 40 }}>
              <div className="h2">Loading...</div>
              <div className="p">Fetching today's headlines and matched clips.</div>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (error || !active) {
    return (
      <>
        <style>{CSS_TEXT}</style>
        <main>
          <div className="topbar">
            <div className="topbar-inner">
              <div className="brand">
                <div className="logo">✦</div>
                <div>
                  <h1 style={{ margin: 0, fontSize: 16, lineHeight: 1.1 }}>VaultBot Daily Drip</h1>
                  <div className="sub">{libraryLabel}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="wrap">
            <div className="card" style={{ textAlign: "center", padding: 40 }}>
              <div className="h2">No Drip Today</div>
              <div className="p">{error || "Check back after 6:00 AM for today's headlines."}</div>
              <div className="pill" style={{ marginTop: 16, display: "inline-flex" }}>
                ⏳ Next drop: {dropTime}
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <style>{CSS_TEXT}</style>
      <main>
        {/* Topbar */}
        <div className="topbar">
          <div className="topbar-inner">
            <div className="brand">
              <div className="logo">✦</div>
              <div>
                <h1 style={{ margin: 0, fontSize: 16, lineHeight: 1.1 }}>VaultBot Daily Drip</h1>
                <div className="sub">{libraryLabel}</div>
              </div>
            </div>
            <div className="pill purple">📅 {dateString()}</div>
          </div>
        </div>

        <div className="wrap">
          {/* Green pill - right under banner */}
          <div className="row" style={{ marginTop: 0, marginBottom: 14 }}>
            <div className="pill green">✨ New matches daily at 6:00am</div>
          </div>

          {/* How this works */}
          <div className="card">
            <div className="h2">How this works</div>

            <div className="flow">
              <span>News</span>
              <span className="dot">→</span>
              <span>AI match</span>
              <span className="dot">→</span>
              <span>Archive clip</span>
              <span className="dot">→</span>
              <span>Community thread</span>
            </div>

            <div className="p">
              Every day VaultBot scans <b>top industry news headlines</b>.
            </div>
            <div className="p">
              VaultBot then searches <b>{libraryLabel}</b> to find the most semantically relevant matches.
            </div>
            <div className="row">
              <div className="pill blue">📚 {libraryLabel}</div>
              <div className="pill purple">⏰ 6am–11pm</div>
              <div className="pill">👤 You: {GUEST_NAME}</div>
            </div>

            <div className="row">
              <div className="pill">⏳ Next drop: {dropTime}</div>
            </div>

            <div className="note">
              Office-hours community. At 11pm the room closes and activity resets.{" "}
              <b>No history is stored.</b>
            </div>
          </div>

          {/* Headline card */}
          <div className="headline-card">
            <div className="headline-topline">
              <span className="headline-label">Headline:</span>
              <span className="headline-date">{dateString()}</span>
              {active.source && <span className="headline-source">{active.source}</span>}
            </div>
            <div className="headline-title">{active.title}</div>
            <div className="ai-map">VaultBot matched the headline above to the quote below from {libraryLabel.toLowerCase()}.</div>
            <div className="quote">"{active.quote}"</div>
            <div className="meta">{active.meta}</div>
            <div className="tags">
              {(active.tags || []).map((t) => (
                <div key={t} className="tag">{t}</div>
              ))}
            </div>
          </div>

          {/* Mapped clip */}
          <div className="card clip-card">
            <div className="clip-header">
              <div className="section-title">AI matched clip below</div>
              <div className="toggle-pill">
                <button
                  type="button"
                  className={mode === "watch" ? "active" : ""}
                  onClick={() => setMode("watch")}
                >
                  Watch
                </button>
                <button
                  type="button"
                  className={mode === "read" ? "active" : ""}
                  onClick={() => setMode("read")}
                >
                  Read
                </button>
              </div>
            </div>

            {mode === "watch" ? (
              <div className="video-wrap">
                {active.playbackId ? (
                  <MuxPlayer
                    ref={playerRef}
                    playbackId={active.playbackId}
                    startTime={timecodeToSeconds(active.windowStart)}
                    preload="metadata"
                    accentColor="#2F7BFF"
                    className="mux-player-drip"
                    metadata={{ video_title: active.title }}
                  />
                ) : (
                  <div className="video">
                    ▶ No clip available
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                  <div className="pill small">Window: {windowLabel}</div>
                </div>
              </div>
            ) : (
              <div className="readbox" style={{ display: "block" }}>
                <div className="quote">"{active.quote}"</div>
              </div>
            )}
          </div>

          {/* More headlines */}
          <div className="card" style={{ marginTop: 14 }}>
            <div className="section-title">More headlines</div>
            <div className="muted2" style={{ marginTop: 8 }}>
              Scroll and tap any headline to load its mapped clip.
            </div>
            <div className="scroller">
              {headlines.map((h, idx) => (
                <div
                  key={idx}
                  className={"mini" + (idx === activeIdx ? " active" : "")}
                  onClick={() => switchHeadline(idx)}
                >
                  <div className="t">{h.title}</div>
                  <div className="m">
                    Mapped clip{h.windowStart ? ` · ${fmtTC(h.windowStart)}` : ""}
                    {h.windowEnd ? `–${fmtTC(h.windowEnd)}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Community thread */}
          <div className="card" style={{ marginTop: 14 }}>
            <div className="section-title">Community thread</div>
            <div className="muted2" style={{ marginTop: 8 }}>
              Comments are tied to the selected headline + mapped clip.
            </div>

            <div className="chat" ref={threadRef}>
              {comments.map((m, i) => (
                <div key={i} className="msg">
                  <div className="avatar">{avatarInitials(m.name)}</div>
                  <div>
                    <div className="name">{m.name}</div>
                    <div className="bubble">{m.text}</div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && (
                <div className="muted2" style={{ textAlign: "center", padding: 20, fontSize: 14 }}>
                  No comments yet. Be the first to share your take.
                </div>
              )}
            </div>

            <div className="chatbar">
              <input
                value={threadInput}
                onChange={(e) => setThreadInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendThread()}
                placeholder='Add a comment… (try: "This changes rights pricing.")'
              />
              <button className="btn primary" type="button" onClick={sendThread}>
                Send
              </button>
            </div>
          </div>

          {/* Chat with the library */}
          <div className="card qa">
            <div className="section-title">Chat with the library</div>
            <div className="muted2" style={{ marginTop: 8 }}>
              Type in your query below and select the method of curation.
            </div>

            <div className="chat" ref={qaRef}>
              {qaLog.map((m, i) => (
                <div key={i} className={"msg" + (m.who === "you" ? " you" : "")}>
                  {m.who !== "you" && <div className="avatar">V</div>}
                  <div>
                    <div className="name">{m.who === "you" ? GUEST_NAME : "@vaultbot"}</div>
                    <div className="bubble">{m.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="chatbar">
              <input
                value={qaInput}
                onChange={(e) => setQaInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendQA()}
                placeholder='Ask: "What does this mean for D2C bundles?"'
                disabled={qaLoading}
              />
              <button className="btn primary" type="button" onClick={sendQA} disabled={qaLoading}>
                {qaLoading ? "..." : "Ask"}
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
              <div className="toggle-pill">
                <button
                  type="button"
                  className={qaMode === "keyword" ? "active" : ""}
                  onClick={() => setQaMode("keyword")}
                >
                  Keyword
                </button>
                <button
                  type="button"
                  className={qaMode === "ai" ? "active" : ""}
                  onClick={() => setQaMode("ai")}
                >
                  AI Match
                </button>
              </div>
            </div>
          </div>

          <div className="footer">
            Powered by VaultBot · Headlines matched to archive via AI
          </div>
        </div>
      </main>
    </>
  );
}

/* ------------------------------------------------------------------ */
const CSS_TEXT = `
  .community-drip-scope,
  .community-drip-scope *,
  *[class^="topbar"],
  *[class^="wrap"],
  *[class^="card"],
  *[class^="headline"],
  *[class^="clip"],
  *[class^="flow"],
  *[class^="pill"],
  *[class^="mini"],
  *[class^="tag"],
  *[class^="btn"],
  *[class^="chat"],
  *[class^="msg"],
  *[class^="bubble"],
  *[class^="avatar"],
  *[class^="toggle"],
  *[class^="video"],
  *[class^="readbox"],
  *[class^="scroller"],
  *[class^="qa"],
  *[class^="footer"] {
    box-sizing: border-box;
  }

  main {
    --bg: #070A12;
    --border: rgba(255,255,255,.10);
    --text: #f8fafc;
    --muted: #cbd5e1;
    --muted2: #94a3b8;
    --blue: #2F7BFF;
    --purple: #8B5CF6;
    --green: #22C55E;
    --shadow: 0 14px 40px -26px rgba(0,0,0,.95);
    background: var(--bg);
    color: var(--text);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }

  .wrap { max-width: 420px; margin: 0 auto; padding: 16px 14px 64px }

  .topbar {
    position: sticky; top: 0; z-index: 20;
    backdrop-filter: blur(10px);
    background: rgba(7,10,18,.75);
    border-bottom: 1px solid var(--border);
  }
  .topbar-inner {
    max-width: 420px; margin: 0 auto; padding: 12px 14px;
    display: flex; justify-content: space-between; gap: 10px; align-items: center;
  }
  .brand { display: flex; gap: 10px; align-items: center }
  .logo {
    width: 38px; height: 38px; border-radius: 16px;
    border: 1px solid var(--border);
    background: linear-gradient(135deg, rgba(47,123,255,.25), rgba(255,255,255,.06), rgba(139,92,246,.20));
    box-shadow: var(--shadow);
    display: grid; place-items: center;
    font-weight: 900;
  }
  .brand .sub { font-size: 13px; color: var(--muted2); margin-top: 3px }

  .pill {
    display: inline-flex; gap: 8px; align-items: center;
    border: 1px solid var(--border);
    background: rgba(255,255,255,.05);
    padding: 8px 12px; border-radius: 999px;
    font-size: 13px; color: var(--text);
    box-shadow: 0 8px 24px -20px rgba(0,0,0,.7);
    white-space: nowrap;
  }
  .pill.small { padding: 6px 10px; font-size: 12px }
  .pill.blue { border-color: rgba(47,123,255,.45); background: rgba(47,123,255,.18) }
  .pill.purple { border-color: rgba(139,92,246,.45); background: rgba(139,92,246,.18) }
  .pill.green { border-color: rgba(34,197,94,.40); background: rgba(34,197,94,.14) }

  .card {
    background: linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,.03));
    border: 1px solid var(--border);
    border-radius: 22px;
    padding: 18px;
    box-shadow: var(--shadow);
  }

  .h2 { font-size: 34px; letter-spacing: -.02em; font-weight: 950; margin: 0 }
  .section-title { font-size: 18px; font-weight: 950; margin: 0 }
  .muted { color: var(--muted) }
  .muted2 { color: var(--muted2) }
  .p { font-size: 16px; line-height: 1.6; color: var(--muted); margin: 14px 0 0 }

  .flow {
    margin-top: 16px;
    padding: 14px;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(0,0,0,.20);
    font-weight: 950;
    font-size: 18px;
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: center;
  }
  .dot { opacity: .6 }
  .row { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-top: 14px }
  .note { margin-top: 14px; font-size: 14px; color: var(--muted2); line-height: 1.45 }

  .headline-card {
    margin-top: 14px;
    border-radius: 22px;
    border: 1px solid var(--border);
    background:
      radial-gradient(1200px 420px at 10% -10%, rgba(47,123,255,.14), transparent 50%),
      radial-gradient(900px 340px at 90% 0%, rgba(139,92,246,.16), transparent 55%),
      linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,.03));
    box-shadow: var(--shadow);
    padding: 18px;
  }
  .headline-topline {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 10px;
    font-size: 13px;
    color: var(--muted2);
  }
  .headline-label {
    font-weight: 900;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: .04em;
  }
  .headline-date {
    opacity: .7;
  }
  .headline-source {
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    font-size: 11px;
  }
  .headline-title {
    font-weight: 950;
    font-size: 34px;
    line-height: 1.08;
    margin: 0;
    letter-spacing: -.02em;
  }
  .ai-map {
    margin-top: 12px;
    font-size: 18px;
    font-weight: 900;
    color: #93c5fd;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .quote {
    margin-top: 14px;
    padding: 14px 14px 14px 16px;
    border-left: 4px solid rgba(47,123,255,.75);
    color: var(--muted);
    font-style: italic;
    line-height: 1.45;
    background: rgba(0,0,0,.18);
    border-radius: 16px;
    font-size: 22px;
  }
  .meta { font-size: 16px; color: var(--muted2); margin-top: 14px }
  .tags { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 14px }
  .tag {
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    font-weight: 900;
    font-size: 16px;
  }

  .clip-card { margin-top: 14px }
  .clip-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .toggle-pill {
    display: inline-flex;
    gap: 4px;
    padding: 4px;
    border-radius: 999px;
    background: rgba(255,255,255,.06);
    border: 1px solid rgba(255,255,255,.10);
  }
  .toggle-pill button {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 700;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: var(--muted2);
    cursor: pointer;
    transition: all .15s;
  }
  .toggle-pill button.active {
    background: rgba(47,123,255,.25);
    color: var(--text);
  }

  .video-wrap {
    margin-top: 14px;
  }
  mux-player.mux-player-drip {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 18px;
    overflow: hidden;
    --media-object-fit: cover;
  }
  .video {
    background: black;
    height: 280px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9aa3b2;
    border: 1px solid rgba(255,255,255,.08);
    position: relative;
    overflow: hidden;
    font-size: 22px;
    font-weight: 900;
  }
  .video .controls {
    position: absolute; left: 14px; right: 14px; bottom: 14px;
    display: flex; justify-content: space-between; gap: 12px; align-items: center; flex-wrap: wrap;
  }
  .btn {
    border: 1px solid rgba(255,255,255,.12);
    background: rgba(255,255,255,.10);
    color: var(--text);
    border-radius: 16px;
    padding: 12px 14px;
    font-size: 16px;
    font-weight: 950;
    cursor: pointer;
    display: inline-flex; gap: 10px; align-items: center;
    user-select: none;
  }
  .btn.primary {
    background: linear-gradient(90deg, rgba(47,123,255,.25), rgba(139,92,246,.20));
  }
  .readbox .quote { font-size: 20px; margin-top: 0 }

  .scroller {
    margin-top: 14px;
    display: flex;
    gap: 12px;
    overflow: auto;
    padding-bottom: 6px;
    scroll-snap-type: x mandatory;
  }
  .mini {
    min-width: 290px;
    scroll-snap-align: start;
    border-radius: 18px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    padding: 14px;
    cursor: pointer;
  }
  .mini.active { border-color: rgba(47,123,255,.55); background: rgba(47,123,255,.10) }
  .mini .t { font-weight: 950; font-size: 14px; line-height: 1.25 }
  .mini .m { margin-top: 8px; color: var(--muted2); font-size: 12px }

  .chatbar { display: flex; gap: 10px; margin-top: 12px }
  main input, main textarea {
    flex: 1;
    height: 48px;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    color: var(--text);
    padding: 0 14px;
    outline: none;
    font-size: 15px;
  }
  main textarea {
    height: 84px;
    padding: 12px 14px;
    resize: none;
  }
  main input::placeholder, main textarea::placeholder { color: var(--muted2) }

  .chat {
    margin-top: 14px;
    background: rgba(0,0,0,.25);
    border: 1px solid rgba(255,255,255,.10);
    padding: 14px;
    border-radius: 18px;
    max-height: 260px;
    overflow: auto;
  }
  .msg { margin: 10px 0; display: flex; gap: 10px; align-items: flex-start }
  .msg.you { justify-content: flex-end }
  .avatar {
    width: 28px; height: 28px; border-radius: 10px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    display: grid; place-items: center;
    font-weight: 950; font-size: 12px; color: var(--text);
    flex: 0 0 auto;
  }
  .bubble {
    max-width: 78%;
    padding: 12px;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,.10);
    background: rgba(255,255,255,.06);
    color: var(--text);
    font-size: 15px;
    line-height: 1.35;
  }
  .msg.you .bubble { background: rgba(255,255,255,.10) }
  .name {
    font-size: 12px;
    color: var(--muted2);
    margin: 2px 0 6px;
    font-weight: 900;
  }

  .qa { margin-top: 14px }
  .qa .bubble { max-width: 100% }
  .footer { padding-top: 14px; text-align: center; color: var(--muted2); font-size: 12px; line-height: 1.35 }
`;
